import { useMemo } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, Clock, Rocket, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";

type Status = "done" | "current" | "upcoming";

const ROADMAP: {
  title: string;
  status: Status;
  items: { name: string; desc: string }[];
}[] = [
  {
    title: "Completed",
    status: "done",
    items: [
      { name: "Ticket System", desc: "Categories, transcripts, auto-close, staff feedback, plus /adduser & /removeuser inside tickets." },
      { name: "Suggestions Board", desc: "Up/down voting that persists across restarts, with staff approve / reject." },
      { name: "Moderation Suite", desc: "Ban, kick, mute, warn, purge, lock, slowmode and blacklist." },
      { name: "Security & Anti-Nuke", desc: "Anti-raid plus limits on mass bans, channel and role deletions." },
      { name: "Server Logging", desc: "Audit logs for messages, roles, channels, members, voice and bans." },
      { name: "Economy & Minigames", desc: "Blackjack, Slots, Roulette, Mines, Coinflip, Dice, Hangman, Memory, Tic-Tac-Toe, Battleship and more." },
      { name: "Engagement Tools", desc: "Giveaways, Polls, Daily Messages, Reaction & Sticky Roles, Welcome/Goodbye and Voice Rewards." },
      { name: "Applications & Invites", desc: "Whitelist / application forms and full invite tracking." },
      { name: "Server Backups", desc: "Create and import backups in Discord — and browse them right in the dashboard." },
      { name: "Alerts & Stats", desc: "Free game alerts, game updates, social alerts, FiveM/RP status and live stat channels." },
      { name: "AI Assistant", desc: "FAQ auto-answers on Discord and the AI Server Architect." },
      { name: "Music Bot", desc: "Full music playback with queues and playlists." },
      { name: "Dashboard & Platform", desc: "Web panel, Custom Commands, Embed Builder, Webhook Scheduler, Partnerships, multi-language, White-Label identity and automated premium (Lemon Squeezy)." },
    ],
  },
  {
    title: "In Progress",
    status: "current",
    items: [
      { name: "Ticket Analytics", desc: "Insights into response times, resolution rates and staff activity." },
      { name: "Real-time Dashboard Notifications", desc: "Live alerts for tickets and moderation events in the panel." },
    ],
  },
  {
    title: "Future Goals",
    status: "upcoming",
    items: [
      { name: "Advanced Server Analytics", desc: "Deep insights into server growth and user activity." },
      { name: "Areon Mobile App", desc: "Full bot management from your phone." },
      { name: "More Minigames & Economy", desc: "New games and economy features on the way." },
    ],
  },
];

const STATUS_META: Record<
  Status,
  { label: string; icon: typeof CheckCircle2; dot: string; text: string; ring: string; glow: string }
> = {
  done: {
    label: "Shipped",
    icon: CheckCircle2,
    dot: "bg-emerald-500",
    text: "text-emerald-400",
    ring: "ring-emerald-500/30",
    glow: "from-emerald-500/15",
  },
  current: {
    label: "Building now",
    icon: Clock,
    dot: "bg-sky-500",
    text: "text-sky-400",
    ring: "ring-sky-500/30",
    glow: "from-sky-500/15",
  },
  upcoming: {
    label: "Planned",
    icon: Circle,
    dot: "bg-muted-foreground/50",
    text: "text-muted-foreground",
    ring: "ring-border",
    glow: "from-primary/10",
  },
};

export function Roadmap() {
  const counts = useMemo(() => {
    const done = ROADMAP.find((s) => s.status === "done")?.items.length ?? 0;
    const current = ROADMAP.find((s) => s.status === "current")?.items.length ?? 0;
    const upcoming = ROADMAP.find((s) => s.status === "upcoming")?.items.length ?? 0;
    const total = done + current + upcoming;
    return { done, current, upcoming, total, pct: total ? Math.round((done / total) * 100) : 0 };
  }, []);

  return (
    <div className="mx-auto max-w-3xl py-8 sm:py-12">
      {/* ===== Hero ===== */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8"
      >
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-primary">
            <Rocket className="h-3 w-3" />
            Product roadmap
          </div>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
            Where Areon is headed
          </h1>
          <p className="mt-2 text-[13px] sm:text-sm text-muted-foreground max-w-xl leading-relaxed">
            A live look at what's shipped, what we're building right now, and what's next.
          </p>

          {/* Progress */}
          <div className="mt-6">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-medium text-foreground/80">Overall progress</span>
              <span className="text-muted-foreground tabular-nums">{counts.pct}% shipped</span>
            </div>
            <div className="h-2 w-full rounded-full bg-muted/60 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${counts.pct}%` }}
                transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-primary"
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-2.5">
              <StatChip value={counts.done} label="Shipped" tint="text-emerald-400" dot="bg-emerald-500" />
              <StatChip value={counts.current} label="In progress" tint="text-sky-400" dot="bg-sky-500" />
              <StatChip value={counts.upcoming} label="Planned" tint="text-muted-foreground" dot="bg-muted-foreground/50" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* ===== Sections ===== */}
      <div className="mt-8 space-y-6">
        {ROADMAP.map((section, si) => {
          const meta = STATUS_META[section.status];
          const Icon = meta.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: si * 0.05 }}
            >
              <Card className="overflow-hidden border-border/50 bg-card/50">
                {/* Section header */}
                <div className={`relative flex items-center justify-between gap-3 px-5 py-4 border-b border-border/40 bg-gradient-to-r ${meta.glow} to-transparent`}>
                  <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-xl bg-card flex items-center justify-center ring-1 ${meta.ring}`}>
                      <Icon className={`h-4.5 w-4.5 ${meta.text} ${section.status === "current" ? "animate-pulse" : ""}`} />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold leading-tight">{section.title}</h2>
                      <span className={`text-[11px] font-medium ${meta.text}`}>{meta.label}</span>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-muted-foreground tabular-nums">
                    {section.items.length} {section.items.length === 1 ? "item" : "items"}
                  </span>
                </div>

                {/* Timeline */}
                <div className="p-5">
                  <div className="relative space-y-5 pl-6">
                    <span className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-border/60" />
                    {section.items.map((item) => (
                      <div key={item.name} className="relative group">
                        <span className={`absolute -left-6 top-1 h-2.5 w-2.5 rounded-full ${meta.dot} ring-4 ring-card`} />
                        <h3 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          {item.name}
                        </h3>
                        <p className="text-[13px] text-muted-foreground mt-0.5 leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* ===== Footer note ===== */}
      <div className="mt-8 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-primary/70" />
        Roadmap is indicative and may shift as priorities evolve.
      </div>
    </div>
  );
}

function StatChip({
  value,
  label,
  tint,
  dot,
}: {
  value: number;
  label: string;
  tint: string;
  dot: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-xl border border-border/50 bg-background/40 px-3 py-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      <span className="text-sm font-semibold tabular-nums">{value}</span>
      <span className={`text-xs ${tint}`}>{label}</span>
    </div>
  );
}
